/**
 * MoveAccessPropertyPage — Origin & Destination property details for residential moves.
 *
 * Each card includes:
 *  • Editable address (Google Places autocomplete)
 *  • Smart-fill from RentCast (auto-populate property details from address)
 *  • Property type (dropdown), square footage, bedrooms, bathrooms, stories,
 *    garage spaces, total rooms, basement, pool, elevator, long carry
 *
 * Only shown for residential moves (quote.type === 'residential').
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  ChevronDown,
  LoaderCircle,
  Sparkles,
  ToggleLeft,
  ToggleRight,
  X,
} from 'lucide-react';
import { useSharedQuote } from '../../context/QuoteContext';
import { useUserProfile } from '../../context/UserProfileContext';
import { AddressSearch, type SelectedPlace } from '../../components/ui/AddressSearch';
import { CustomDropdown } from '../../components/ui/CustomDropdown';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { ReadOnlyBanner } from '../../components/ui/ReadOnlyBanner';
import { api } from '../../lib/api';
import {
  QuoteAddress,
  RentCastPropertyData,
  ResidentialPropertyDetails,
  ResidentialPropertyType,
} from '../../types/admin';

// ── Constants ────────────────────────────────────────────────────────────────

const PROPERTY_TYPE_OPTIONS: { value: ResidentialPropertyType | ''; label: string }[] = [
  { value: '', label: 'Select type…' },
  { value: 'single_family', label: 'Single Family' },
  { value: 'condo', label: 'Condo' },
  { value: 'townhouse', label: 'Townhouse' },
  { value: 'apartment', label: 'Apartment' },
  { value: 'manufactured', label: 'Manufactured' },
  { value: 'multi_family', label: 'Multi-Family' },
];

const RES_PROP_DEFAULTS: ResidentialPropertyDetails = {
  bedrooms: undefined,
  bathrooms: undefined,
  basement: false,
  stories: undefined,
  elevator: false,
  garageSpaces: undefined,
  totalRoomCount: undefined,
  pool: false,
  longCarry: false,
};

// ── RentCast mapping ─────────────────────────────────────────────────────────

const RENTCAST_PROPERTY_TYPE_MAP: Record<string, ResidentialPropertyType> = {
  'Single Family': 'single_family',
  'Townhouse': 'townhouse',
  'Condo': 'condo',
  'Condominium': 'condo',
  'Apartment': 'apartment',
  'Manufactured': 'manufactured',
  'Mobile Home': 'manufactured',
  'Multi-Family': 'multi_family',
};

function mapRentCastToProperty(data: RentCastPropertyData): Partial<ResidentialPropertyDetails> {
  const patch: Partial<ResidentialPropertyDetails> = {};
  if (data.propertyType) {
    const mapped = RENTCAST_PROPERTY_TYPE_MAP[data.propertyType];
    if (mapped) patch.propertyType = mapped;
  }
  if (data.bedrooms != null) patch.bedrooms = data.bedrooms;
  if (data.bathrooms != null) patch.bathrooms = data.bathrooms;
  if (data.squareFootage != null) patch.squareFootage = data.squareFootage;

  const f = data.features as Record<string, unknown> | undefined;
  if (f) {
    if (typeof f.floorCount === 'number' && f.floorCount > 0) patch.stories = f.floorCount;
    if (typeof f.garageSpaces === 'number') {
      patch.garageSpaces = f.garageSpaces;
    } else if (typeof f.garageType === 'string' && f.garageType.toLowerCase() !== 'none') {
      patch.garageSpaces = typeof f.garage === 'boolean' && f.garage ? 1 : (f.garageType ? 1 : 0);
    } else if (typeof f.garage === 'boolean') {
      patch.garageSpaces = f.garage ? 1 : 0;
    }
    if (typeof f.roomCount === 'number') patch.totalRoomCount = f.roomCount;
    if (typeof f.pool === 'boolean') patch.pool = f.pool;
  }
  return patch;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatFeatureValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return value.toLocaleString();
  if (typeof value === 'string') return value || '—';
  if (Array.isArray(value)) return value.join(', ') || '—';
  return JSON.stringify(value);
}

function formatFeatureKey(key: string): string {
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim();
}

// ── Sub-components ───────────────────────────────────────────────────────────

function FieldLabel({
  htmlFor,
  children,
  optional,
}: {
  htmlFor: string;
  children: React.ReactNode;
  optional?: boolean;
}) {
  return (
    <label htmlFor={htmlFor} className="block text-[11px] font-bold uppercase tracking-widest text-slate">
      {children}
      {optional && <span className="text-slate font-normal normal-case tracking-normal ml-1">(optional)</span>}
    </label>
  );
}

function CollapsibleSection({
  title,
  defaultOpen = false,
  headerAction,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  headerAction?: React.ReactNode;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-visible">
      <div className={`flex items-center justify-between px-4 py-4
                   ${open ? 'rounded-t-xl' : 'rounded-xl'}`}>
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="flex-1 flex items-center justify-between
                     hover:opacity-80 transition-opacity duration-150 pointer-events-auto"
        >
          <span className="font-semibold text-small text-midnight">{title}</span>
          <ChevronDown
            size={18}
            className={`text-slate transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          />
        </button>
        {headerAction && (
          <div className="ml-2 shrink-0" onClick={e => e.stopPropagation()}>
            {headerAction}
          </div>
        )}
      </div>
      {open && (
        <div className="px-4 pb-5 pt-1 flex flex-col gap-4 border-t border-stone overflow-x-hidden">
          {children}
        </div>
      )}
    </div>
  );
}

function PropertyDetailsModal({
  data,
  error,
  onClose,
}: {
  data: RentCastPropertyData | null;
  error?: string | null;
  onClose: () => void;
}) {
  const rows: { label: string; value: string }[] = data ? [
    { label: 'Address', value: data.formattedAddress ?? '—' },
    { label: 'Property Type', value: data.propertyType ?? '—' },
    { label: 'Bedrooms', value: data.bedrooms != null ? String(data.bedrooms) : '—' },
    { label: 'Bathrooms', value: data.bathrooms != null ? String(data.bathrooms) : '—' },
    { label: 'Square Footage', value: data.squareFootage != null ? data.squareFootage.toLocaleString() + ' sq ft' : '—' },
    { label: 'Lot Size', value: data.lotSize != null ? data.lotSize.toLocaleString() + ' sq ft' : '—' },
    { label: 'Year Built', value: data.yearBuilt != null ? String(data.yearBuilt) : '—' },
    { label: 'County', value: data.county ?? '—' },
    { label: 'Owner(s)', value: data.ownerNames?.join(', ') || '—' },
    { label: 'Legal Description', value: data.legalDescription ?? '—' },
  ] : [];

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/40" onClick={onClose} aria-hidden />
      <div className="relative bg-white rounded-t-2xl shadow-2xl max-h-[85vh] flex flex-col
                      md:rounded-2xl md:max-w-md md:w-full md:max-h-[70vh]">
        <div className="flex items-center justify-between px-4 py-4 border-b border-stone shrink-0">
          <p className="font-semibold text-small text-midnight">Imported Property Details</p>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-stone
                       text-slate hover:text-midnight active:bg-mist transition-colors"
            aria-label="Close"
          >
            <X size={15} />
          </button>
        </div>
        <div className="overflow-y-auto px-4 py-4 flex flex-col gap-3">
          {error && (
            <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-rose/10 border border-rose/30">
              <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
              <p className="text-small text-rose">{error}</p>
            </div>
          )}
          {!error && rows.map(r => (
            <div key={r.label}>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate">{r.label}</p>
              <p className="text-small text-midnight mt-0.5">{r.value}</p>
            </div>
          ))}
          {!error && data?.features && Object.keys(data.features).length > 0 && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate mb-2">Features</p>
              <div className="bg-mist rounded-xl px-3 py-2 flex flex-col gap-1.5">
                {Object.entries(data.features).map(([key, val]) => (
                  <div key={key} className="flex justify-between gap-3">
                    <span className="text-[12px] text-slate shrink-0">{formatFeatureKey(key)}</span>
                    <span className="text-[12px] text-midnight text-right">{formatFeatureValue(val)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {!error && (
            <p className="text-[11px] text-slate text-center mt-2">
              Data provided by RentCast
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Property Card ────────────────────────────────────────────────────────────

function PropertyCard({
  direction,
  idPrefix,
  address,
  onAddressChange,
  property,
  onPropertyChange,
  rentCastData,
  rentCastLoading,
  onSmartFill,
  onViewImported,
  isReadOnly,
}: {
  direction: 'origin' | 'destination';
  idPrefix: string;
  address: SelectedPlace | null;
  onAddressChange: (place: SelectedPlace | null) => void;
  property: ResidentialPropertyDetails;
  onPropertyChange: (patch: Partial<ResidentialPropertyDetails>) => void;
  rentCastData: RentCastPropertyData | null;
  rentCastLoading: boolean;
  onSmartFill: () => void;
  onViewImported: () => void;
  isReadOnly: boolean;
}) {
  const title = direction === 'origin' ? 'Origin Property Details' : 'Destination Property Details';
  const addressLabel = direction === 'origin' ? 'Origin Address' : 'Destination Address';

  const smartFillButton = address && !isReadOnly ? (
    <button
      type="button"
      onClick={onSmartFill}
      disabled={rentCastLoading}
      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold
                 text-plum bg-plum/5 border border-plum/20
                 hover:bg-plum/10 active:bg-plum/15
                 transition-colors duration-150 disabled:opacity-50"
      title="Auto-fill property details from address"
      aria-label={`Smart fill ${direction} property details`}
    >
      {rentCastLoading
        ? <LoaderCircle size={13} className="animate-spin" />
        : <Sparkles size={13} />
      }
      Smart Fill
    </button>
  ) : undefined;

  return (
    <CollapsibleSection title={title} defaultOpen headerAction={smartFillButton}>
      <div className="flex flex-col gap-4">
        {/* Address */}
        <div className="flex flex-col gap-1.5">
          <AddressSearch
            id={`${idPrefix}-addr`}
            value={address}
            onChange={onAddressChange}
            label={addressLabel}
          />
          {rentCastData && (
            <button
              type="button"
              onClick={onViewImported}
              className="text-[12px] text-plum hover:underline text-left mt-1"
            >
              View Imported Property Details
            </button>
          )}
        </div>

        {/* Property Type — dropdown */}
        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor={`${idPrefix}-ptype`} optional>Property Type</FieldLabel>
          <CustomDropdown
            id={`${idPrefix}-ptype`}
            options={PROPERTY_TYPE_OPTIONS}
            value={property.propertyType ?? ''}
            onChange={v => onPropertyChange({ propertyType: (v || undefined) as ResidentialPropertyType | undefined })}
            placeholder="Select type…"
          />
        </div>

        {/* Square Footage */}
        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor={`${idPrefix}-sqft`} optional>Square Footage</FieldLabel>
          <input
            id={`${idPrefix}-sqft`}
            type="number"
            min="0"
            step="1"
            value={property.squareFootage ?? ''}
            onChange={e => {
              const v = e.target.value;
              onPropertyChange({ squareFootage: v === '' ? undefined : parseInt(v, 10) });
            }}
            placeholder="e.g. 1500"
            className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                       text-small text-midnight placeholder:text-slate
                       focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                       transition-colors duration-150"
          />
        </div>

        {/* Bedrooms + Bathrooms */}
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor={`${idPrefix}-bed`} optional>Bedrooms</FieldLabel>
            <input
              id={`${idPrefix}-bed`}
              type="number"
              min="0"
              step="1"
              value={property.bedrooms ?? ''}
              onChange={e => {
                const v = e.target.value;
                onPropertyChange({ bedrooms: v === '' ? undefined : parseInt(v, 10) });
              }}
              placeholder="2"
              className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                         text-small text-midnight placeholder:text-slate
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors duration-150"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor={`${idPrefix}-bath`} optional>Bathrooms</FieldLabel>
            <input
              id={`${idPrefix}-bath`}
              type="number"
              min="0"
              step="0.5"
              value={property.bathrooms ?? ''}
              onChange={e => {
                const v = e.target.value;
                onPropertyChange({ bathrooms: v === '' ? undefined : parseFloat(v) });
              }}
              placeholder="1"
              className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                         text-small text-midnight placeholder:text-slate
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors duration-150"
            />
          </div>
        </div>

        {/* Stories + Garage Spaces */}
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor={`${idPrefix}-stories`} optional>Stories</FieldLabel>
            <input
              id={`${idPrefix}-stories`}
              type="number"
              min="0"
              step="1"
              value={property.stories ?? ''}
              onChange={e => {
                const v = e.target.value;
                onPropertyChange({ stories: v === '' ? undefined : parseInt(v, 10) });
              }}
              placeholder="0"
              className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                         text-small text-midnight placeholder:text-slate
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors duration-150"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor={`${idPrefix}-garage`} optional>Garage Spaces</FieldLabel>
            <input
              id={`${idPrefix}-garage`}
              type="number"
              min="0"
              step="1"
              value={property.garageSpaces ?? ''}
              onChange={e => {
                const v = e.target.value;
                onPropertyChange({ garageSpaces: v === '' ? undefined : parseInt(v, 10) });
              }}
              placeholder="0"
              className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                         text-small text-midnight placeholder:text-slate
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors duration-150"
            />
          </div>
        </div>

        {/* Total Rooms */}
        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor={`${idPrefix}-rooms`} optional>Total Rooms</FieldLabel>
          <input
            id={`${idPrefix}-rooms`}
            type="number"
            min="0"
            step="1"
            value={property.totalRoomCount ?? ''}
            onChange={e => {
              const v = e.target.value;
              onPropertyChange({ totalRoomCount: v === '' ? undefined : parseInt(v, 10) });
            }}
            placeholder="0"
            className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                       text-small text-midnight placeholder:text-slate
                       focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                       transition-colors duration-150"
          />
        </div>

        {/* Toggle fields */}
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => onPropertyChange({ basement: !property.basement })}
            className="flex items-center gap-2"
          >
            {property.basement
              ? <ToggleRight size={26} className="text-plum" />
              : <ToggleLeft size={26} className="text-slate" />
            }
            <span className="text-small font-medium text-midnight">Basement</span>
          </button>
          <button
            type="button"
            onClick={() => onPropertyChange({ pool: !property.pool })}
            className="flex items-center gap-2"
          >
            {property.pool
              ? <ToggleRight size={26} className="text-plum" />
              : <ToggleLeft size={26} className="text-slate" />
            }
            <span className="text-small font-medium text-midnight">Pool</span>
          </button>
          <button
            type="button"
            onClick={() => onPropertyChange({ elevator: !property.elevator })}
            className="flex items-center gap-2"
          >
            {property.elevator
              ? <ToggleRight size={26} className="text-plum" />
              : <ToggleLeft size={26} className="text-slate" />
            }
            <span className="text-small font-medium text-midnight">Elevator Required</span>
          </button>
          <button
            type="button"
            onClick={() => onPropertyChange({ longCarry: !property.longCarry })}
            className="flex items-center gap-2"
          >
            {property.longCarry
              ? <ToggleRight size={26} className="text-plum" />
              : <ToggleLeft size={26} className="text-slate" />
            }
            <span className="text-small font-medium text-midnight">Long Carry</span>
          </button>
        </div>
      </div>
    </CollapsibleSection>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function MoveAccessPropertyPage() {
  const navigate = useNavigate();
  const { activeOrg } = useUserProfile();
  const orgId = activeOrg?.orgId ?? null;
  const { quote, loading, updateQuote } = useSharedQuote();

  const [initialized, setInitialized] = useState(false);
  const justInitialized = useRef(false);

  // Addresses
  const [originPlace, setOriginPlace] = useState<SelectedPlace | null>(null);
  const [destPlace, setDestPlace] = useState<SelectedPlace | null>(null);

  // Property details
  const [originProperty, setOriginProperty] = useState<ResidentialPropertyDetails>({ ...RES_PROP_DEFAULTS });
  const [destProperty, setDestProperty] = useState<ResidentialPropertyDetails>({ ...RES_PROP_DEFAULTS });

  // RentCast
  const [originRentCast, setOriginRentCast] = useState<RentCastPropertyData | null>(null);
  const [destRentCast, setDestRentCast] = useState<RentCastPropertyData | null>(null);
  const [rentCastLoading, setRentCastLoading] = useState<'origin' | 'destination' | null>(null);
  const [rentCastModal, setRentCastModal] = useState<'origin' | 'destination' | null>(null);
  const [rentCastError, setRentCastError] = useState<{ direction: 'origin' | 'destination'; message: string } | null>(null);

  // Save state
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const internalMove = quote?.addresses?.internalMove ?? false;

  // ── Initialize from quote ────────────────────────────────────────────────
  useEffect(() => {
    if (quote && !loading && !initialized) {
      justInitialized.current = true;

      const orig = quote.addresses?.origin;
      if (orig) {
        setOriginPlace({ address: orig.address, placeId: orig.placeId });
        if (orig.residentialProperty) setOriginProperty({ ...RES_PROP_DEFAULTS, ...orig.residentialProperty });
        if (orig.importedPropertyData) setOriginRentCast(orig.importedPropertyData);
      }

      const dest = quote.addresses?.destination;
      if (dest) {
        setDestPlace({ address: dest.address, placeId: dest.placeId });
        if (dest.residentialProperty) setDestProperty({ ...RES_PROP_DEFAULTS, ...dest.residentialProperty });
        if (dest.importedPropertyData) setDestRentCast(dest.importedPropertyData);
      }

      setInitialized(true);
    }
  }, [quote, loading, initialized]);

  // ── Build address object for save ────────────────────────────────────────
  function buildAddress(
    place: SelectedPlace | null,
    existingAddr: QuoteAddress | undefined,
    property: ResidentialPropertyDetails,
    importedData: RentCastPropertyData | null,
  ): QuoteAddress | undefined {
    if (!place) return existingAddr;
    return {
      ...existingAddr,
      address: place.address,
      placeId: place.placeId,
      residentialProperty: property,
      importedPropertyData: importedData ?? undefined,
    };
  }

  // ── Auto-save ────────────────────────────────────────────────────────────
  const doAutoSave = useCallback(() => {
    if (quote?.quoteStatus !== 'draft') return;
    setSaving(true);
    setSaveError('');

    const existingOrigin = quote.addresses?.origin;
    const existingDest = quote.addresses?.destination;

    const originAddr = buildAddress(originPlace, existingOrigin, originProperty, originRentCast);
    const destAddr = internalMove
      ? buildAddress(originPlace, existingDest, destProperty, destRentCast)
      : buildAddress(destPlace, existingDest, destProperty, destRentCast);

    updateQuote({
      addresses: {
        ...quote.addresses,
        origin: originAddr,
        destination: destAddr,
      },
    })
      .then(() => setSaving(false))
      .catch(err => {
        setSaveError(err instanceof Error ? err.message : 'Auto-save failed');
        setSaving(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    originPlace, destPlace, originProperty, destProperty,
    originRentCast, destRentCast, internalMove,
    updateQuote, quote?.quoteStatus,
    quote?.addresses,
  ]);

  useEffect(() => {
    if (!initialized) return;
    if (justInitialized.current) { justInitialized.current = false; return; }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(doAutoSave, 800);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [doAutoSave, initialized]);

  // ── RentCast smart fill ──────────────────────────────────────────────────
  async function handleSmartFill(direction: 'origin' | 'destination') {
    const address = direction === 'origin' ? originPlace?.address : (internalMove ? originPlace?.address : destPlace?.address);
    if (!address || !orgId) return;
    setRentCastLoading(direction);
    setRentCastError(null);
    try {
      const res = await api.get<{ success: boolean; data: RentCastPropertyData | null }>(
        `/quotes/${orgId}/property-lookup?address=${encodeURIComponent(address)}`,
      );
      const data = res.data;
      if (!data) {
        setRentCastLoading(null);
        setRentCastError({ direction, message: 'No property data found for this address.' });
        setRentCastModal(direction);
        return;
      }
      const patch = mapRentCastToProperty(data);
      if (direction === 'origin') {
        setOriginRentCast(data);
        setOriginProperty(prev => ({ ...prev, ...patch }));
      } else {
        setDestRentCast(data);
        setDestProperty(prev => ({ ...prev, ...patch }));
      }
      setRentCastModal(direction);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not fetch property details.';
      setRentCastError({ direction, message });
      setRentCastModal(direction);
    }
    setRentCastLoading(null);
  }

  // ── Render ───────────────────────────────────────────────────────────────

  if (loading || !quote) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const isReadOnly = quote.quoteStatus !== 'draft';

  return (
    <div className="px-4 pt-4 pb-8 max-w-2xl">
      {/* Mobile back */}
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="md:hidden flex items-center gap-1 text-small text-slate hover:text-midnight
                   mb-3 transition-colors duration-150 -ml-1"
      >
        <ArrowLeft size={16} />
        Back
      </button>

      <div className="flex items-center justify-between mb-5">
        <h1 className="font-display text-h1 text-midnight">Access & Property Details</h1>
        {saving && <LoaderCircle size={16} className="text-slate animate-spin shrink-0" />}
      </div>

      {isReadOnly && <ReadOnlyBanner status={quote.quoteStatus} />}

      {saveError && (
        <div className="mb-4 flex items-start gap-2 px-4 py-3 rounded-xl bg-rose/10 border border-rose/30">
          <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
          <p className="text-small text-rose">{saveError}</p>
        </div>
      )}

      <div className={isReadOnly ? 'pointer-events-none opacity-60' : ''}>
        <div className="flex flex-col gap-3">
          {/* Origin Property Details */}
          <PropertyCard
            direction="origin"
            idPrefix="ap-orig"
            address={originPlace}
            onAddressChange={setOriginPlace}
            property={originProperty}
            onPropertyChange={patch => setOriginProperty(prev => ({ ...prev, ...patch }))}
            rentCastData={originRentCast}
            rentCastLoading={rentCastLoading === 'origin'}
            onSmartFill={() => handleSmartFill('origin')}
            onViewImported={() => setRentCastModal('origin')}
            isReadOnly={isReadOnly}
          />

          {/* Destination Property Details */}
          <PropertyCard
            direction="destination"
            idPrefix="ap-dest"
            address={internalMove ? originPlace : destPlace}
            onAddressChange={internalMove ? setOriginPlace : setDestPlace}
            property={destProperty}
            onPropertyChange={patch => setDestProperty(prev => ({ ...prev, ...patch }))}
            rentCastData={destRentCast}
            rentCastLoading={rentCastLoading === 'destination'}
            onSmartFill={() => handleSmartFill('destination')}
            onViewImported={() => setRentCastModal('destination')}
            isReadOnly={isReadOnly}
          />
        </div>
      </div>

      {/* RentCast property details modal */}
      {rentCastModal === 'origin' && (originRentCast || rentCastError?.direction === 'origin') && (
        <PropertyDetailsModal
          data={originRentCast}
          error={rentCastError?.direction === 'origin' ? rentCastError.message : null}
          onClose={() => { setRentCastModal(null); setRentCastError(null); }}
        />
      )}
      {rentCastModal === 'destination' && (destRentCast || rentCastError?.direction === 'destination') && (
        <PropertyDetailsModal
          data={destRentCast}
          error={rentCastError?.direction === 'destination' ? rentCastError.message : null}
          onClose={() => { setRentCastModal(null); setRentCastError(null); }}
        />
      )}
    </div>
  );
}
